/**
 * CraftPage.js — калькулятор крафта L2 Harbor
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../api/supabase';
import RECIPES from '../data/recipes_all.json';

const fmt = n => Number(n || 0).toLocaleString('ru-RU');
const ITEMS_PER_PAGE = 10;
const GRADES = ['Все', 'D', 'C', 'B', 'A', 'S'];
const GRADE_COLORS = { D: '#94a3b8', C: '#34d399', B: '#60a5fa', A: '#a78bfa', S: '#fbbf24' };
const CRAFT_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

// ── Магазинные товары (фиксированная цена, всегда в наличии) ─────────────────
const SHOP_PRICES = {
  'Gemstone D': 1250,
  'Gemstone C': 3750,
  'Gemstone B': 12500,
  'Soul Ore':   312,
  'Spirit Ore': 500,
};

// ── Является ли материал куском вещи ─────────────────────────────────────────
// Кусок: нет sub_materials, название содержит слова из названия крафтимой вещи
// Название вещи = recipe.item_name без "Recipe: "
function isPiece(matName, itemName) {
  if (!matName || !itemName) return false;
  if (matName.startsWith('Recipe:')) return false;
  if (matName === itemName) return false;
  // Берём слова из названия вещи (минимум 3 символа)
  const words = itemName.split(/[\s']+/).filter(w => w.length >= 3);
  if (!words.length) return false;
  const matLower = matName.toLowerCase();
  // Кусок должен содержать хотя бы 2 слова из названия вещи (или 1 если оно длинное)
  const matchCount = words.filter(w => matLower.includes(w.toLowerCase())).length;
  return matchCount >= Math.min(2, words.length);
}

// ── Строим priceMap с учётом магазинных цен ──────────────────────────────────
function buildPriceMap(rawData, matNames) {
  const map = {};

  // Сначала данные из Supabase — группируем SELL записи по списку
  const sellsByItem = {};
  (rawData || []).forEach(r => {
    const n = r.item_name;
    if (!map[n]) map[n] = { sell_entries: [], buy_price: null };
    if (r.mode === 'SELL' && r.price > 0) {
      if (!sellsByItem[n]) sellsByItem[n] = [];
      sellsByItem[n].push({ price: r.price, count: Number(r.count) || 1, trader: r.trader, city: r.city });
    }
    if (r.mode === 'BUY' && r.price > 0) {
      if (!map[n].buy_price || r.price > map[n].buy_price) {
        map[n].buy_price = r.price;
      }
    }
  });

  // Сортируем SELL по цене
  matNames.forEach(n => {
    if (!map[n]) map[n] = { sell_entries: [], buy_price: null };
    map[n].sell_entries = (sellsByItem[n] || []).sort((a, b) => a.price - b.price);
    // min цена и трейдер (первый в отсортированном списке)
    if (map[n].sell_entries.length > 0) {
      map[n].sell_price  = map[n].sell_entries[0].price;
      map[n].sell_trader = map[n].sell_entries[0].trader;
      map[n].sell_city   = map[n].sell_entries[0].city;
      map[n].sell_total_stock = map[n].sell_entries.reduce((s, e) => s + e.count, 0);
    }
  });

  // Добавляем/перезаписываем магазинные товары
  Object.entries(SHOP_PRICES).forEach(([name, price]) => {
    if (!map[name]) map[name] = { sell_entries: [], buy_price: null };
    map[name].sell_price   = price;
    map[name].sell_trader  = 'Магазин';
    map[name].sell_city    = null;
    map[name].sell_entries = [{ price, count: 999999, trader: 'Магазин', city: null }];
    map[name].sell_total_stock = 999999;
    map[name].is_shop = true;
  });

  return map;
}

// ── Рассчитать список продавцов для нужного кол-ва ───────────────────────────
// Возвращает { entries: [{trader,city,price,qty}], total_cost, available }
function resolveSupply(entries, needed) {
  if (!entries || entries.length === 0) return { entries: [], total_cost: null, available: 0 };
  let remaining = needed;
  const result = [];
  let total = 0;
  for (const e of entries) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, e.count);
    result.push({ trader: e.trader, city: e.city, price: e.price, qty: take });
    total += e.price * take;
    remaining -= take;
  }
  return { entries: result, total_cost: remaining > 0 ? null : total, available: needed - remaining };
}

// ── Рекурсивный расчёт стоимости ─────────────────────────────────────────────
// mode: 'sell' | 'buy'
function calcCost(materials, priceMap, mode = 'sell') {
  if (!materials || !materials.length) return null;
  let total = 0;
  for (const m of materials) {
    const qty = Number(m.total_needed) || Number(m.qty_step) || 1;
    const pd = priceMap[m.name];
    const hasSubs = m.sub_materials && m.sub_materials.length > 0;

    let unitPrice = null;
    if (mode === 'sell') {
      unitPrice = pd?.sell_price || null;
    } else {
      unitPrice = pd?.buy_price || pd?.sell_price || null;
    }

    const subCost = hasSubs ? calcCost(m.sub_materials, priceMap, mode) : null;
    const directCost = unitPrice ? unitPrice * qty : null;

    if (subCost !== null && (directCost === null || subCost < directCost)) {
      total += subCost;
    } else if (directCost !== null) {
      total += directCost;
    } else {
      return null;
    }
  }
  return total;
}

// ── Сколько можно скрафтить прямо сейчас ─────────────────────────────────────
function calcMaxCrafts(materials, priceMap) {
  if (!materials || !materials.length) return 0;
  let minCrafts = Infinity;
  for (const m of materials) {
    const qty = Number(m.total_needed) || Number(m.qty_step) || 1;
    const pd = priceMap[m.name];
    const hasSubs = m.sub_materials && m.sub_materials.length > 0;

    if (hasSubs) {
      // Можно скрафтить подматериал — не ограничиваем (магазинные или крафт)
      const subCrafts = calcMaxCrafts(m.sub_materials, priceMap);
      const craftsFromSub = Math.floor(subCrafts / qty);
      minCrafts = Math.min(minCrafts, craftsFromSub);
    } else {
      const stock = pd?.sell_total_stock || 0;
      const possible = Math.floor(stock / qty);
      minCrafts = Math.min(minCrafts, possible);
    }
  }
  return minCrafts === Infinity ? 0 : minCrafts;
}

// ── Можно ли получить материал (купить или скрафтить) ────────────────────────
function canCraftItem(material, priceMap) {
  const pd = priceMap[material.name];
  // Есть прямая цена — доступен
  if (pd?.sell_price) return true;
  // Есть подматериалы — проверяем все рекурсивно
  if (material.sub_materials && material.sub_materials.length > 0) {
    return material.sub_materials.every(sub => canCraftItem(sub, priceMap));
  }
  return false;
}

function allAvailable(materials, priceMap) {
  if (!materials || !materials.length) return true;
  return materials.every(m => canCraftItem(m, priceMap));
}

// ── Собрать плоский список покупок на рынке (SELL) ───────────────────────────
function collectSellList(materials, priceMap, result = {}) {
  for (const m of materials) {
    const qty = Number(m.total_needed) || Number(m.qty_step) || 1;
    const pd  = priceMap[m.name];
    const hasSubs = m.sub_materials && m.sub_materials.length > 0;
    const subCost = hasSubs ? calcCost(m.sub_materials, priceMap, 'sell') : null;
    const directCost = pd?.sell_price ? pd.sell_price * qty : null;
    // Используем компоненты если: subCost считается И (нет прямой цены ИЛИ компоненты дешевле)
    const useComponents = subCost !== null && (directCost === null || subCost < directCost);
    // Можно скрафтить даже если subCost=null (нет цен для расчёта, но материалы есть)
    const canMakeFromSubs = hasSubs && canCraftItem(m, priceMap) && !pd?.sell_price;

    if (useComponents || canMakeFromSubs) {
      collectSellList(m.sub_materials, priceMap, result);
    } else if (pd?.sell_price) {
      if (!result[m.name]) result[m.name] = { name: m.name, qty: 0, entries: pd.sell_entries || [] };
      result[m.name].qty += qty;
    } else {
      // Реально недоступно
      if (!result[m.name]) result[m.name] = { name: m.name, qty: 0, entries: [], missing: true };
      result[m.name].qty += qty;
    }
  }
  return result;
}

// ── Считаем частичную сумму + нехватку ───────────────────────────────────────
function calcPartialCost(sellList) {
  let total = 0;
  const shortage = []; // { name, have, need }

  for (const r of Object.values(sellList)) {
    if (r.missing) continue;
    const supply = resolveSupply(r.entries, r.qty);
    if (supply.total_cost !== null) {
      total += supply.total_cost;
    } else {
      // Частично хватает
      const partialCost = supply.entries.reduce((s, e) => s + e.price * e.qty, 0);
      total += partialCost;
      shortage.push({ name: r.name, have: supply.available, need: r.qty });
    }
  }
  return { total, shortage };
}

// ── Считаем частичную сумму для BUY списка ───────────────────────────────────
function calcPartialBuyCost(buyList) {
  let total = 0;
  const missing = [];
  for (const r of Object.values(buyList)) {
    if (r.missing) { missing.push(r.name); continue; }
    total += (r.price || 0) * r.qty;
  }
  return { total, missing };
}

// ── Собрать плоский список для BUY-магазина ───────────────────────────────────
function collectBuyList(materials, priceMap, result = {}, itemName = '') {
  for (const m of materials) {
    const qty = Number(m.total_needed) || Number(m.qty_step) || 1;
    const pd  = priceMap[m.name];
    const hasSubs = m.sub_materials && m.sub_materials.length > 0;
    const isShop  = pd?.is_shop || false;
    const buyPrice = pd?.buy_price || null;
    const shopPrice = isShop ? pd.sell_price : null;
    const buyMap = {};
    Object.entries(priceMap).forEach(([key2, val2]) => { buyMap[key2] = { ...val2, sell_price: val2.buy_price || val2.sell_price }; });
    const subCost = hasSubs ? calcCost(m.sub_materials, buyMap, 'sell') : null;

    // Для куска без BUY цены — смотрим SELL цену
    const piece = !hasSubs && isPiece(m.name, itemName);
    const sellFallback = piece && !buyPrice && pd?.sell_price ? pd.sell_price : null;

    const effectivePrice = buyPrice || shopPrice || sellFallback || null;
    const directCost = effectivePrice ? effectivePrice * qty : null;
    const useComponents = subCost !== null && (directCost === null || subCost < directCost);
    const canMakeFromSubs = hasSubs && canCraftItem(m, priceMap) && !effectivePrice;

    if (useComponents || canMakeFromSubs) {
      collectBuyList(m.sub_materials, priceMap, result, itemName);
    } else if (effectivePrice) {
      const key = m.name + (isShop ? '_shop' : '');
      if (!result[key]) result[key] = {
        name: m.name, qty: 0, price: effectivePrice, isShop,
        isSellFallback: !!sellFallback,  // помечаем что цена с рынка SELL
      };
      result[key].qty += qty;
    } else {
      if (!result[m.name]) result[m.name] = { name: m.name, qty: 0, price: null, missing: true };
      result[m.name].qty += qty;
    }
  }
  return result;
}

// ── Собрать все материалы рекурсивно ─────────────────────────────────────────
function collectMaterials(materials, result = new Set()) {
  for (const m of materials) {
    result.add(m.name);
    if (m.sub_materials?.length) collectMaterials(m.sub_materials, result);
  }
  return result;
}

// ── Дерево материалов ─────────────────────────────────────────────────────────
function MaterialTree({ materials, priceMap, depth = 0 }) {
  if (!materials || !materials.length) return null;
  return (
    <div style={{ marginLeft: depth * 16 }}>
      {materials.map((m, i) => {
        const hasSubs = m.sub_materials && m.sub_materials.length > 0;
        const qty     = Number(m.total_needed) || Number(m.qty_step) || 1;
        const pd      = priceMap[m.name];
        const sellPrice  = pd?.sell_price  || null;
        const sellTrader = pd?.sell_trader || null;
        const sellCity   = pd?.sell_city   || null;
        const buyPrice   = pd?.buy_price   || null;
        const isShop     = pd?.is_shop     || false;
        const subCost    = hasSubs ? calcCost(m.sub_materials, priceMap, 'sell') : null;
        const useComponents = subCost !== null && (sellPrice === null || subCost < sellPrice * qty);

        return (
          <div key={i} style={{ padding: '5px 8px', marginBottom: 2, background: depth % 2 === 0 ? 'rgba(255,255,255,.02)' : 'rgba(59,130,246,.03)', borderLeft: `2px solid ${depth === 0 ? 'var(--border2)' : 'var(--border)'}`, borderRadius: '0 3px 3px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: hasSubs ? 'var(--blue2)' : 'var(--muted)', fontSize: 10, marginTop: 2, flexShrink: 0 }}>{hasSubs ? '▼' : '◆'}</span>
              <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--text)', fontSize: 13, minWidth: 160 }}>
                {m.name}
                <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 6, fontFamily: 'JetBrains Mono,monospace', fontSize: 11 }}>×{qty}</span>
                {isShop && <span style={{ marginLeft: 6, fontSize: 10, color: '#a78bfa', fontFamily: 'Rajdhani,sans-serif' }}>🏪</span>}
              </span>
              {Object.keys(priceMap).length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {sellPrice && (
                    <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono,monospace' }}>
                      <span style={{ color: 'var(--muted)', fontSize: 10 }}>SELL </span>
                      <span style={{ color: useComponents ? 'var(--muted)' : 'var(--green)', textDecoration: useComponents ? 'line-through' : 'none' }}>{fmt(sellPrice * qty)} ₳</span>
                      {sellTrader && !isShop && <span style={{ color: 'var(--muted)', fontSize: 10, marginLeft: 4 }}>{sellTrader}{sellCity && <span style={{ color: GRADE_COLORS['C'], marginLeft: 3 }}>[{sellCity}]</span>}</span>}
                    </span>
                  )}
                  {buyPrice && (
                    <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono,monospace' }}>
                      <span style={{ color: 'var(--muted)', fontSize: 10 }}>BUY </span>
                      <span style={{ color: 'var(--gold)' }}>{fmt(buyPrice * qty)} ₳</span>
                    </span>
                  )}
                  {useComponents && subCost !== null && (
                    <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'Rajdhani,sans-serif', background: 'rgba(52,211,153,.1)', border: '1px solid rgba(52,211,153,.2)', borderRadius: 2, padding: '1px 5px' }}>
                      ✓ крафт {fmt(subCost)} ₳ выгоднее
                    </span>
                  )}
                  {!sellPrice && !buyPrice && <span style={{ color: 'var(--muted)', fontSize: 10, fontFamily: 'Rajdhani,sans-serif' }}>нет на рынке</span>}
                </div>
              )}
            </div>
            {hasSubs && <MaterialTree materials={m.sub_materials} priceMap={priceMap} depth={depth + 1} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Карточка рецепта ──────────────────────────────────────────────────────────
function RecipeCard({ recipe, globalPriceMap, isGreen }) {
  const [open,      setOpen]      = useState(false);
  const [priceMap,  setPriceMap]  = useState(globalPriceMap || {});
  const [loading,   setLoading]   = useState(false);
  const [totalSell, setTotalSell] = useState(null);
  const [totalBuy,  setTotalBuy]  = useState(null);
  const [sellList,  setSellList]  = useState(null);
  const [buyList,   setBuyList]   = useState(null);

  const gradeColor = GRADE_COLORS[recipe.grade] || 'var(--muted)';
  const itemName   = recipe.item_name.replace(/^Recipe:\s*/i, '');

  // Если пришёл globalPriceMap — обновляем
  useEffect(() => {
    if (globalPriceMap && Object.keys(globalPriceMap).length > 0) {
      setPriceMap(globalPriceMap);
      const sell = calcCost(recipe.recipe_tree, globalPriceMap, 'sell');
      setTotalSell(sell);
      const buy = calcCost(recipe.recipe_tree, globalPriceMap, 'buy');
      setTotalBuy(buy);
      setSellList(collectSellList(recipe.recipe_tree, globalPriceMap, {}));
      setBuyList(collectBuyList(recipe.recipe_tree, globalPriceMap, {}, itemName));
    }
  }, [globalPriceMap, recipe, itemName]);

  const handleCalc = useCallback(async () => {
    setLoading(true);
    const matNames = Array.from(collectMaterials(recipe.recipe_tree));
    const { data } = await supabase
      .from('market_prices')
      .select('item_name, price, count, mode, trader, city')
      .in('item_name', matNames)
      .gt('price', 0);

    const pm = buildPriceMap(data, matNames);
    setPriceMap(pm);

    setTotalSell(calcCost(recipe.recipe_tree, pm, 'sell'));
    setSellList(collectSellList(recipe.recipe_tree, pm, {}));
    const buy = calcCost(recipe.recipe_tree, pm, 'buy');
    setTotalBuy(buy);
    setBuyList(collectBuyList(recipe.recipe_tree, pm, {}, itemName));

    setOpen(true);
    setLoading(false);
  }, [recipe, itemName]);

  const cardBorder = isGreen ? '1px solid rgba(52,211,153,.4)' : '1px solid var(--border)';
  const cardBg     = isGreen ? 'rgba(52,211,153,.04)' : 'var(--panel)';

  return (
    <div style={{ background: cardBg, border: cardBorder, borderRadius: 4, marginBottom: 10, overflow: 'hidden' }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', flexWrap: 'wrap', gap: 8, background: 'linear-gradient(90deg,var(--panel2),transparent)', borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: '2px 7px', borderRadius: 2, color: gradeColor, background: gradeColor + '18', border: `1px solid ${gradeColor}40` }}>{recipe.grade}</span>
          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, color: isGreen ? 'var(--green)' : 'var(--text)', fontSize: 14 }}>{itemName}</span>
          {recipe.crystal_count && Number(recipe.crystal_count) > 0 && (
            <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'JetBrains Mono,monospace' }}>💎 {recipe.crystal_count}</span>
          )}
          {isGreen && <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'Rajdhani,sans-serif', background: 'rgba(52,211,153,.1)', border: '1px solid rgba(52,211,153,.2)', borderRadius: 2, padding: '1px 6px' }}>✓ все есть</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {totalSell !== null && <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--green)' }}>📤 {fmt(totalSell)} ₳</span>}
          {totalBuy  !== null && <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--gold)'  }}>📥 {fmt(totalBuy)} ₳</span>}
          <button onClick={handleCalc} disabled={loading} style={{ padding: '5px 14px', fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', background: 'rgba(59,130,246,.15)', border: '1px solid rgba(59,130,246,.4)', color: 'var(--blue2)', borderRadius: 3, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? '⏳' : '🧮 Рассчитать'}
          </button>
          <button onClick={() => setOpen(o => !o)} style={{ padding: '5px 12px', fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700, background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 3, cursor: 'pointer' }}>
            {open ? '▲ Скрыть' : '▼ Состав'}
          </button>
        </div>
      </div>

      {/* Содержимое */}
      {open && (
        <div style={{ padding: '10px 16px' }}>
          <MaterialTree materials={recipe.recipe_tree} priceMap={priceMap} />

          {/* ── 📤 Купить на рынке ── */}
          {sellList && (
            <div style={{ marginTop: 14, background: 'rgba(52,211,153,.05)', border: '1px solid rgba(52,211,153,.2)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(52,211,153,.15)', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1 }}>📤 Купить на рынке</span>
                {(() => { const miss = Object.values(sellList).filter(r => r.missing); return miss.length > 0 && <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--red)' }}>⚠ не учтено: {miss.map(r => r.name).join(', ')}</span>; })()}
              </div>
              <div style={{ padding: '8px 14px' }}>
                {Object.values(sellList).filter(r => !r.missing).sort((a, b) => {
                  const ac = resolveSupply(a.entries, a.qty).total_cost || 0;
                  const bc = resolveSupply(b.entries, b.qty).total_cost || 0;
                  return bc - ac;
                }).map((r, i) => {
                  const supply = resolveSupply(r.entries, r.qty);
                  return (
                    <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: supply.entries.length > 1 ? 4 : 0 }}>
                        <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--text)', fontSize: 13, minWidth: 160 }}>
                          {r.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>×{r.qty}</span>
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: supply.total_cost ? 'var(--green)' : 'var(--red)' }}>
                          {supply.total_cost ? fmt(supply.total_cost) + ' ₳' : `⚠ хватает только ${supply.available}/${r.qty}`}
                        </span>
                      </div>
                      {supply.entries.map((e, j) => (
                        <div key={j} style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 16, marginTop: 2, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--muted)' }}>×{e.qty}</span>
                          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--green)' }}>{fmt(e.price)} ₳/шт</span>
                          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 600, color: '#7dd3fc' }}>
                            {e.trader}
                            {e.city && <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 4 }}>[{e.city}]</span>}
                          </span>
                          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--muted)' }}>= {fmt(e.price * e.qty)} ₳</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              {/* Итого внизу */}
              {(() => {
                const { total, shortage } = calcPartialCost(sellList);
                const missing = Object.values(sellList).filter(r => r.missing);
                return (
                  <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(52,211,153,.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Купить на рынке будет стоить:</span>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 18, fontWeight: 700, color: (shortage.length > 0 || missing.length > 0) ? 'var(--gold)' : 'var(--green)' }}>
                        {fmt(total)} ₳
                      </span>
                    </div>
                    {shortage.length > 0 && (
                      <div style={{ marginTop: 6, fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--red)' }}>
                        ⚠ не хватает на рынке: {shortage.map(s => `${s.name} (есть ${fmt(s.have)} из ${fmt(s.need)})`).join(', ')}
                      </div>
                    )}
                    {missing.length > 0 && (
                      <div style={{ marginTop: 4, fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--red)' }}>
                        ⚠ нет данных по: {missing.map(r => r.name).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── 📥 Купить самому в городе ── */}
          {buyList && (
            <div style={{ marginTop: 10, background: 'rgba(251,191,36,.05)', border: '1px solid rgba(251,191,36,.2)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(251,191,36,.15)', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1 }}>📥 Купить самому в городе</span>
                {(() => { const miss = Object.values(buyList).filter(r => r.missing); return miss.length > 0 && <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--red)' }}>⚠ не учтено: {miss.map(r => r.name).join(', ')}</span>; })()}
              </div>
              <div style={{ padding: '8px 14px' }}>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Выставь магазин BUY с этими ценами:</div>
                {Object.values(buyList).filter(r => !r.missing).sort((a, b) => (b.price * b.qty) - (a.price * a.qty)).map((r, i) => (
                  <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--text)', fontSize: 13, minWidth: 160 }}>
                        {r.name} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>×{r.qty}</span>
                      </span>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: r.isSellFallback ? 'var(--green)' : 'var(--gold)' }}>
                        {fmt(r.price * r.qty)} ₳
                      </span>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--muted)' }}>({fmt(r.price)} за шт.)</span>
                    </div>
                    {r.isSellFallback && (
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 10, color: 'var(--green)', opacity: 0.8, paddingLeft: 2, marginTop: 2 }}>
                        ℹ цена рассчитана по рыночной стоимости куска (SELL)
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Итого внизу */}
              {(() => {
                const { total, missing } = calcPartialBuyCost(buyList);
                const hasMissing = missing.length > 0;
                return (
                  <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(251,191,36,.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Купить самому в городе будет стоить:</span>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 18, fontWeight: 700, color: hasMissing ? 'var(--gold)' : '#fbbf24' }}>
                        {fmt(total)} ₳
                      </span>
                    </div>
                    {hasMissing && (
                      <div style={{ marginTop: 6, fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--red)' }}>
                        ⚠ нет данных по: {missing.join(', ')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Экран входа ───────────────────────────────────────────────────────────────
function CraftLogin({ onAuth }) {
  const [pw, setPw]       = useState('');
  const [err, setErr]     = useState(false);
  const handle = () => {
    if (pw === CRAFT_PASSWORD) { sessionStorage.setItem('craftAuth', '1'); onAuth(); }
    else { setErr(true); setPw(''); }
  };
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 320 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 24, fontWeight: 700, color: 'var(--blue3)', letterSpacing: 3, textTransform: 'uppercase', textAlign: 'center', marginBottom: 24 }}>
          🔨 Крафт L2 Harbor
        </div>
        <div className="pane">
          <div className="pane-head">🔐 Доступ</div>
          <div className="pane-body">
            <div className="admin-form">
              <input type="password" placeholder="Пароль" value={pw}
                onChange={e => { setPw(e.target.value); setErr(false); }}
                onKeyDown={e => e.key === 'Enter' && handle()}
                style={{ borderColor: err ? 'var(--red)' : '' }} autoFocus />
              {err && <div style={{ color: 'var(--red)', fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>Неверный пароль</div>}
              <button onClick={handle}>ВОЙТИ</button>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <a href="/" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>← На главную</a>
        </div>
      </div>
    </div>
  );
}

// ── Основная страница ─────────────────────────────────────────────────────────
export function CraftPage() {
  const [auth, setAuth] = useState(() => sessionStorage.getItem('craftAuth') === '1');
  if (!auth) return <CraftLogin onAuth={() => setAuth(true)} />;
  return <CraftInner />;
}

function CraftInner() {
  const [search,      setSearch]      = useState('');
  const [grade,       setGrade]       = useState('Все');
  const [page,        setPage]        = useState(1);
  const [craftMode,   setCraftMode]   = useState(false);
  const [craftPrices, setCraftPrices] = useState({});  // item_id → priceMap
  const [craftLoading,setCraftLoading]= useState(false);
  const [craftScores, setCraftScores] = useState({});  // item_id → maxCrafts
  const [greenSet,    setGreenSet]    = useState(new Set());

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return RECIPES.filter(r => {
      if (grade !== 'Все' && r.grade !== grade) return false;
      if (q && !r.item_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [search, grade]);

  // В режиме крафта сортируем по maxCrafts убыванием
  const sorted = useMemo(() => {
    if (!craftMode || Object.keys(craftScores).length === 0) return filtered;
    return [...filtered].sort((a, b) => {
      const aGreen = greenSet.has(a.item_id) ? 1 : 0;
      const bGreen = greenSet.has(b.item_id) ? 1 : 0;
      // Сначала зелёные
      if (bGreen !== aGreen) return bGreen - aGreen;
      // Потом по кол-ву крафтов убыванием
      return (craftScores[b.item_id] || 0) - (craftScores[a.item_id] || 0);
    });
  }, [filtered, craftMode, craftScores, greenSet]);

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const paginated  = sorted.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleSearch = v => { setSearch(v); setPage(1); };
  const handleGrade  = g => { setGrade(g);  setPage(1); };

  // ── Режим крафта: загружаем цены для всех рецептов ───────────────────────
  const handleCraftMode = useCallback(async () => {
    if (craftMode) { setCraftMode(false); return; }
    setCraftLoading(true);

    // Собираем все уникальные материалы по всем рецептам из текущего фильтра
    const allMats = new Set();
    filtered.forEach(r => collectMaterials(r.recipe_tree, allMats));
    const matNames = Array.from(allMats);

    // Один запрос для всех
    const { data } = await supabase
      .from('market_prices')
      .select('item_name, price, count, mode, trader, city')
      .in('item_name', matNames)
      .gt('price', 0);

    // Строим общий priceMap
    const pm = buildPriceMap(data, matNames);

    // Считаем maxCrafts и зелёные для каждого рецепта
    const scores = {};
    const greens = new Set();
    const prices = {};
    filtered.forEach(r => {
      const n = calcMaxCrafts(r.recipe_tree, pm);
      scores[r.item_id] = n;
      if (allAvailable(r.recipe_tree, pm)) greens.add(r.item_id);
      prices[r.item_id] = pm;
    });

    setCraftScores(scores);
    setGreenSet(greens);
    setCraftPrices(prices);
    setCraftMode(true);
    setCraftLoading(false);
    setPage(1);
  }, [filtered, craftMode]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      {/* Шапка */}
      <div style={{ background: 'linear-gradient(180deg,#0d1b35 0%,transparent 100%)', borderBottom: '1px solid var(--border)', padding: '16px 0 12px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--blue3)', letterSpacing: 2, textTransform: 'uppercase' }}>🔨 Крафт L2 Harbor</div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', letterSpacing: 1, marginTop: 2 }}>{RECIPES.length} рецептов · Eternal C1 x1</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href="/market_online" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--blue2)', textDecoration: 'none', letterSpacing: 1, padding: '7px 12px', border: '1px solid rgba(59,130,246,.3)', borderRadius: 3 }}>📦 Рынок</a>
              <a href="/" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textDecoration: 'none', letterSpacing: 1, padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 3 }}>← Главная</a>
            </div>
          </div>

          {/* Фильтры */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder="🔍 Поиск по названию..." value={search} onChange={e => handleSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--panel2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'Noto Serif,serif', fontSize: 13, borderRadius: 3, outline: 'none' }} />
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {GRADES.map(g => {
                const clr = GRADE_COLORS[g] || 'var(--blue)';
                const active = grade === g;
                return (
                  <button key={g} onClick={() => handleGrade(g)} style={{ padding: '6px 12px', background: active ? (g === 'Все' ? 'rgba(59,130,246,.2)' : clr + '22') : 'var(--panel2)', border: `1px solid ${active ? (g === 'Все' ? 'var(--blue)' : clr) : 'var(--border)'}`, color: active ? (g === 'Все' ? 'var(--blue2)' : clr) : 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700, borderRadius: 3, cursor: 'pointer', letterSpacing: 1 }}>{g}</button>
                );
              })}
              <button onClick={handleCraftMode} disabled={craftLoading} style={{ padding: '6px 14px', background: craftMode ? 'rgba(52,211,153,.2)' : 'var(--panel2)', border: `1px solid ${craftMode ? 'rgba(52,211,153,.5)' : 'var(--border)'}`, color: craftMode ? 'var(--green)' : 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700, borderRadius: 3, cursor: craftLoading ? 'wait' : 'pointer', letterSpacing: 1 }}>
                {craftLoading ? '⏳' : craftMode ? '✓ Крафт' : '🔨 Крафт'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Контент */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {[['Найдено', filtered.length], ['Страница', `${page} / ${totalPages || 1}`]].map(([lbl, val]) => (
            <div key={lbl} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 3, padding: '6px 16px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 16, color: 'var(--blue2)', fontWeight: 700 }}>{val}</div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{lbl}</div>
            </div>
          ))}
          {craftMode && (
            <div style={{ background: 'var(--panel)', border: '1px solid rgba(52,211,153,.3)', borderRadius: 3, padding: '6px 16px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 16, color: 'var(--green)', fontWeight: 700 }}>{greenSet.size}</div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Можно скрафтить</div>
            </div>
          )}
        </div>

        {paginated.length === 0
          ? <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', padding: 60, fontSize: 16 }}>Ничего не найдено</div>
          : paginated.map((r, i) => (
            <RecipeCard
              key={r.item_id || i}
              recipe={r}
              globalPriceMap={craftPrices[r.item_id]}
              isGreen={greenSet.has(r.item_id)}
            />
          ))
        }

        {/* Пагинация */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 20, flexWrap: 'wrap' }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={pageBtn(page === 1)}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={pageBtn(page === 1)}>‹</button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
              return <button key={p} onClick={() => setPage(p)} style={pageBtn(false, page === p)}>{p}</button>;
            })}
            <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={pageBtn(page === totalPages)}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtn(page === totalPages)}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}

function pageBtn(disabled, active = false) {
  return { padding: '6px 11px', minWidth: 36, background: active ? 'rgba(59,130,246,.25)' : disabled ? 'var(--panel2)' : 'var(--panel)', border: `1px solid ${active ? 'rgba(59,130,246,.6)' : 'var(--border)'}`, color: active ? 'var(--blue2)' : disabled ? 'var(--border)' : 'var(--text2)', fontFamily: 'JetBrains Mono,monospace', fontSize: 13, borderRadius: 3, cursor: disabled ? 'default' : 'pointer' };
}
